import type { Configuration } from '@azure/msal-browser'

const resolvedEntraClientId =
  import.meta.env.VITE_ENTRA_CLIENT_APP_CLIENT_ID ||
  import.meta.env.VITE_ENTRA_CLIENT_ID ||
  ''

// MSAL configuration for Entra ID authentication
// Values are loaded from environment variables (set by Aspire AppHost)
export const msalConfig: Configuration = {
  auth: {
    clientId: resolvedEntraClientId,
    authority: `https://login.microsoftonline.com/${import.meta.env.VITE_ENTRA_TENANT_ID || 'common'}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return
        switch (level) {
          case 0: // Error
            console.error(message)
            break
          case 1: // Warning
            console.warn(message)
            break
          case 2: // Info
            console.info(message)
            break
          case 3: // Verbose
            console.debug(message)
            break
        }
      },
      // Warning in dev, Error in prod — Info is opt-in via VITE_MSAL_LOG_LEVEL.
      //
      // Info was the dev default and it drowned the console: MSAL logs several lines per token
      // operation and the messages are minified ids ("1sm769") that mean nothing without a lookup
      // table. Hundreds of those hid the real failures — the 431s on /api/activity, /api/observatory/runs
      // and the SignalR negotiate arrived interleaved with token spam and read as noise.
      logLevel: msalLogLevel(),
    },
  },
}

/** MSAL log level: `VITE_MSAL_LOG_LEVEL` (0-3) if set, else Warning in dev and Error in prod. */
function msalLogLevel(): number {
  const configured = Number.parseInt(import.meta.env.VITE_MSAL_LOG_LEVEL ?? '', 10)
  if (Number.isInteger(configured) && configured >= 0 && configured <= 3) {
    return configured
  }

  return import.meta.env.DEV ? 1 : 0
}

// Scopes for API access
// Use {guid}/.default — Entra resolves the GUID to the app registration
// regardless of its Application ID URI (which may be api://lighthouse-api-{env})
const apiClientId = import.meta.env.VITE_ENTRA_API_CLIENT_ID || ''
export const apiScopes = {
    read: apiClientId ? `${apiClientId}/.default` : '',
}

// Login request configuration
// User.Read grants access to Microsoft Graph profile photo
export const loginRequest = {
  scopes: ['openid', 'profile', 'email', 'User.Read'],
}

// Check if Entra ID authentication is configured
export function isEntraConfigured(): boolean {
  return !!(
    resolvedEntraClientId &&
    import.meta.env.VITE_ENTRA_TENANT_ID
  )
}
